import { CtaButton } from '@tassomutuo-backoffice/ui';
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import {
  ComparisonPeriod,
  Period,
} from '../../../services/api/statistics/statistics';
import React, { useEffect } from 'react';
import { TeamGroup, User } from '@tassomutuo-backoffice/types';
import { endOfDay, endOfMonth, startOfMonth, subMonths } from 'date-fns';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

const selectPeriodOptions: { label: string; value: Period | 'custom' }[] = [
  { label: 'Ultimo mese', value: 'last_month' },
  { label: 'Ultimi 3 mesi', value: 'last_3_months' },
  { label: 'Ultimi 6 mesi', value: 'last_6_months' },
  { label: 'Ultimi 12 mesi', value: 'last_12_months' },
  { label: 'Personalizzato', value: 'custom' },
];

const comparisonPeriodOptions: { label: string; value: ComparisonPeriod }[] = [
  { label: 'Periodo precedente', value: 'previous_period' },
  { label: 'Anno precedente', value: 'previous_year' },
];

interface Props {
  title: string;
  subtitle: string;
  selectedPeriod: Period;
  setSelectedPeriod: (period: Period) => void;
  selectedComparisonPeriod?: ComparisonPeriod;
  setSelectedComparisonPeriod?: (period: ComparisonPeriod) => void;
  onExportPdfClick?: () => void;
  onExportCsvClick?: () => void;
  setSelectedMember?: (
    groupId: Pick<User, 'id' | 'hubspotOwnerId' | 'name' | 'email'> | null
  ) => void;
  selectedMember?: Pick<
    User,
    'id' | 'hubspotOwnerId' | 'name' | 'email'
  > | null;
  teamGroups?: TeamGroup[];
  selectedGroupId?: string | null;
  setSelectedGroupId?: (groupId: string | null) => void;
  memberSelectOptions?:
    | Pick<User, 'id' | 'hubspotOwnerId' | 'name' | 'email'>[]
    | undefined;
  hideMemberSelect?: boolean;
  // Props per il date range picker
  dateRange?: DateRange;
  setDateRange?: React.Dispatch<React.SetStateAction<DateRange>>;
  regionsFilterOptions?: string[];
  selectedRegion?: string | null;
  setSelectedRegion?: React.Dispatch<React.SetStateAction<string | null>>;
  provincesFilterOptions?: string[];
  selectedProvince?: string | null;
  setSelectedProvince?: React.Dispatch<React.SetStateAction<string | null>>;
}

const DashboardHeaderSection = ({
  title,
  subtitle,
  selectedPeriod,
  setSelectedPeriod,
  selectedComparisonPeriod,
  setSelectedComparisonPeriod,
  onExportPdfClick,
  onExportCsvClick,
  setSelectedMember,
  selectedMember,
  teamGroups,
  selectedGroupId,
  setSelectedGroupId,
  memberSelectOptions,
  hideMemberSelect = false,
  dateRange,
  setDateRange,
  regionsFilterOptions,
  selectedRegion,
  setSelectedRegion,
  provincesFilterOptions,
  selectedProvince,
  setSelectedProvince,
}: Props) => {
  const [periodMode, setPeriodMode] = React.useState<Period | 'custom'>(
    selectedPeriod
  );

  useEffect(() => {
    setPeriodMode(selectedPeriod);
  }, [selectedPeriod]);

  const handlePeriodModeChange = (value: Period | 'custom') => {
    setPeriodMode(value);

    if (value === 'custom') {
      setSelectedPeriod('custom');

      // Imposta default agli ultimi 3 mesi quando si passa a custom
      const endDate = endOfDay(endOfMonth(subMonths(new Date(), 1)));
      const startDate = startOfMonth(subMonths(endDate, 2));

      setDateRange?.({
        start: startDate,
        end: endDate,
      });
    } else {
      // È un periodo predefinito
      setSelectedPeriod(value as Period);
      setDateRange?.({ start: null, end: null });
    }
  };

  const isCustomDateRangeAvailable = dateRange && setDateRange;
  const isCustomMode = periodMode === 'custom';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#E9EAEB',
          pt: 2,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          color="primary.main"
          px={4}
          pt={1}
          mb={!subtitle ? 6 : 0}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="h6" mb={4} px={4}>
            {subtitle}
          </Typography>
        )}
        <Box
          sx={{
            borderBottom: '1px solid gainsboro',
            pt: 3,
            pb: 3,
            mb: 6,
            width: '100%',
            backgroundColor: '#FAFAFA',
            borderRadius: '8px',
            border: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              px: 4,
            }}
          >
            {/* Select per periodo */}
            <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Periodo</InputLabel>
                <Select
                  sx={{ bgcolor: 'white' }}
                  label="Periodo"
                  value={periodMode}
                  onChange={(e) => {
                    handlePeriodModeChange(e.target.value as Period | 'custom');
                  }}
                >
                  {selectPeriodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date Range Picker - appare solo se "Personalizzato" è selezionato */}
            {isCustomMode && isCustomDateRangeAvailable && (
              <>
                <Box sx={{ flexGrow: 1, minWidth: '180px', maxWidth: '220px' }}>
                  <DatePicker
                    label="Data inizio"
                    value={dateRange.start}
                    onChange={(newValue) =>
                      setDateRange((prev) => ({ ...prev, start: newValue }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { bgcolor: 'white' },
                        size: 'small',
                        inputProps: { readOnly: true },
                      },
                    }}
                    minDate={new Date('2023-09-01')}
                    maxDate={
                      dateRange.end ||
                      endOfDay(endOfMonth(subMonths(new Date(), 1)))
                    }
                  />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: '180px', maxWidth: '220px' }}>
                  <DatePicker
                    label="Data fine"
                    value={dateRange.end}
                    onChange={(newValue) =>
                      setDateRange((prev) => ({ ...prev, end: newValue }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { bgcolor: 'white' },
                        size: 'small',
                        inputProps: { readOnly: true },
                      },
                    }}
                    minDate={dateRange.start || new Date('2023-09-01')}
                    maxDate={endOfDay(endOfMonth(subMonths(new Date(), 1)))}
                  />
                </Box>
              </>
            )}

            {/* Select per periodo di confronto */}
            {selectedComparisonPeriod !== undefined &&
              setSelectedComparisonPeriod && (
                <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Periodo di confronto</InputLabel>
                    <Select
                      sx={{ bgcolor: 'white' }}
                      label="Periodo di confronto"
                      value={selectedComparisonPeriod}
                      onChange={(e) => {
                        setSelectedComparisonPeriod(
                          e.target.value as ComparisonPeriod
                        );
                      }}
                    >
                      {comparisonPeriodOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

            {/* Filtro per gruppo */}
            {teamGroups && teamGroups.length > 0 && (
              <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtra per gruppo</InputLabel>
                  <Select
                    sx={{ bgcolor: 'white' }}
                    label="Filtra per gruppo"
                    value={selectedGroupId || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedGroupId?.(value === '' ? null : value);
                      setSelectedMember?.(null); // Reset member when group changes
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti i gruppi</em>
                    </MenuItem>
                    {teamGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Selezione membro */}
            {!hideMemberSelect &&
              memberSelectOptions &&
              memberSelectOptions.length > 0 && (
                <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) => option.email}
                    options={memberSelectOptions || []}
                    filterOptions={(x) => x}
                    noOptionsText="Nessun utente trovato"
                    onChange={(event, newValue) => {
                      setSelectedMember?.(newValue);
                    }}
                    value={selectedMember}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ bgcolor: 'white' }}
                        label="Membro"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: <>{params.InputProps.endAdornment}</>,
                        }}
                      />
                    )}
                  />
                </Box>
              )}

            {/* Regione */}
            {regionsFilterOptions && regionsFilterOptions?.length > 0 && (
              <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  isOptionEqualToValue={(option, value) => option === value}
                  options={regionsFilterOptions || []}
                  noOptionsText="Nessuna regione trovata"
                  onChange={(event, newValue) => {
                    setSelectedRegion?.(newValue === '' ? null : newValue);
                    setSelectedProvince?.(null); // Reset province when region changes
                  }}
                  value={selectedRegion}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'white' }}
                      label="Regione"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Provincia */}
            {provincesFilterOptions && provincesFilterOptions?.length > 0 && (
              <Box sx={{ flexGrow: 1, minWidth: '220px', maxWidth: '280px' }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  isOptionEqualToValue={(option, value) => option === value}
                  options={provincesFilterOptions || []}
                  noOptionsText="Nessuna provincia trovata"
                  onChange={(event, newValue) => {
                    setSelectedProvince?.(newValue === '' ? null : newValue);
                  }}
                  value={selectedProvince}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'white' }}
                      label="Provincia"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Pulsanti esportazione */}
            {(onExportPdfClick || onExportCsvClick) && (
              <Box sx={{ flexShrink: 0, ml: 'auto', display: 'flex', gap: 1 }}>
                {onExportCsvClick && (
                  <CtaButton
                    variant="outlined"
                    sx={{
                      borderColor: (th) => th.palette.primary.light,
                      color: (th) => th.palette.primary.main,
                      fontWeight: 'bold',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      whiteSpace: 'nowrap',
                    }}
                    onClick={onExportCsvClick}
                  >
                    Esporta in CSV
                  </CtaButton>
                )}
                {onExportPdfClick && (
                  <CtaButton
                    variant="contained"
                    sx={{
                      bgcolor: (th) => th.palette.primary.light,
                      color: (th) => th.palette.primary.contrastText,
                      fontWeight: 'bold',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      whiteSpace: 'nowrap',
                    }}
                    onClick={onExportPdfClick}
                  >
                    Esporta in PDF
                  </CtaButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardHeaderSection;
